import { Space } from 'antd';
import { Button, Card, PageHeader } from 'components';
import { type VFC, useState } from 'react';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { useAppNotifications } from 'hooks';
import type { SerializedHardwarePackage } from '@enmon/commons/lib/tools';
import { HardwarePackageModal } from './components/Modal';
import { useHWPackagesFilterWithPagination } from './hooks/useHWPackagesFilterWithPagination';
import { Icon } from '@/components/icon';
import {
  GET_HARDWARE_PACKAGES_DATA,
  type HardwarePackagesData,
  type HardwarePackagesVariables,
} from './queries/getHardwarePackagesData';
import { HardwarePackagesTable } from './components/Table';

export const HardwarePackagesListPage: VFC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hardwarePackage, setHardwarePackage] = useState<SerializedHardwarePackage>(undefined);

  const appNotification = useAppNotifications();

  const { filter, pagination, setPaginationState } = useHWPackagesFilterWithPagination();
  const { sort } = filter;
  const { current, pageSize } = pagination;

  const {
    data: { hardwarePackages: { data: hardwarePackages = [] } = {} } = {},
    loading: isLoading,
    refetch,
  } = useStrapiQuery<HardwarePackagesData, HardwarePackagesVariables>(GET_HARDWARE_PACKAGES_DATA, {
    variables: {
      sort,
      page: current,
      pageSize,
    },
    onCompleted(response) {
      setPaginationState({
        total: response?.hardwarePackages?.meta?.pagination?.total ?? 0,
      });
    },
    onError(error) {
      console.error(error);
      appNotification.error({
        message: `Error while fetching hardware packages. Please try again.`,
      });
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleEdit = (item: SerializedHardwarePackage) => {
    setIsModalOpen(true);
    setHardwarePackage(item);
  };

  const handleHide = () => {
    setIsModalOpen(false);
    setHardwarePackage(undefined);
  };

  const handleSuccess = () => {
    refetch();
    handleHide();
  };

  return (
    <>
      {isModalOpen && (
        <HardwarePackageModal hardwarePackage={hardwarePackage} onClose={handleHide} onSuccess={handleSuccess} />
      )}
      <Space className="w-100" direction="vertical">
        <PageHeader
          extra={[
            <Button
              key="add-hw-package"
              icon={<Icon iconString="plus" />}
              type="primary"
              onClick={() => setIsModalOpen(true)}
            >
              Add new hardware package
            </Button>,
          ]}
          ghost={false}
          title="Hardware Packages"
        />
        <Card>
          <HardwarePackagesTable
            data={hardwarePackages}
            isLoading={isLoading}
            onEdit={handleEdit}
            onSuccess={refetch}
          />
        </Card>
      </Space>
    </>
  );
};
